import {
    AdaptationPlatformConfig,
    LiveSynchronizationMethod,
    LogLevel,
    PlayerAPI,
    PlayerConfig,
} from 'bitmovin-player';
import { UIManager } from 'bitmovin-player-ui';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import cdnHandler from '../../../helpers/CDNHandler';
import { cdnLog, describePlayer, describeSession, shortUrl } from '../../../helpers/cdn/debug';
import { __ } from '../../../translations';
import { GenericBitmovinPlayerProps } from '../types/IPlayer';
import { useConfigurationSections } from '../../../hooks/useConfiguration';
import playerService from '../../../helpers/bitmovinPlayer';
import h from '../../../helpers/common';
import url from '../../../helpers/url';
import { FlexWrapper, Spinner } from '@intigral/shared';
import { screenDetailTypes } from '@libs/constants';
import { MixpanelContext } from '../../../contexts/MixpanelContext';
import { isLowLatencySource, onAudioTrackChange, onTransitionEnd, sendBookmarks } from '../utils/PlayerUtills';
import useAvod from '../../../hooks/useAvod';
import styles from '../styles/player.module.sass';
import { avodHandler, avodMixpanelEvents } from '../../../helpers/avodHandler';
import LearnManager from '../../../helpers/LearnManager';
import WaterMark from '../../../helpers/watermark';
import useCdnBalancer from '../../../hooks/useCdnBalancer';
import { PlayerError } from './PlayerError';
import { BaseBitmovinPlayer } from './BaseBitimovinPlayer';
import { createBitmovinPlayer } from '../utils/BasePlayerUtils';
import { getDRMConfig } from '../utils/drmConfig';
import { usePlayerEvents } from '../hooks/usePlayerEvents';
import { useCustomUi } from '../hooks/useCustomUi';
import { playerTypes } from '../constants';
import { usePlayer } from '../hooks/usePlayer';
import { usePlayerHandler } from '../hooks/usePlayerHandler';
import { useBookmark } from '../../../hooks';
import useCSAI from '../../../hooks/useCSAI';
import { isPlayerAlive, safePlayerPlay } from '../../../helpers/playerGuards';
import { playerSSAIManager } from '../../../utils/SSAI';
import { EVENT } from '../../../contexts/MixpanelConstants';
import { getMixpanelVodProperties } from '../../../contexts/MixpanelHelper';
import { userAccessControl } from '../../../utils/userAccessControl';
import LowLatencyDebugPanel, { useLowLatencyDebug } from '../debug/LowLatencyDebugPanel';
import { generateSupplyTagsForSSAI } from '../../../utils/supplyTags';

// eslint-disable-next-line prefer-const
let textTrackTimer = null;

const GenericBitmovinPlayer = ({
    VODAsset,
    LinearAsset,
    playerType,
    streamingUrl: initialStreamingUrl,
    contentType,
    playlist,
    defaultEpisodeIndex,
    bookmarkedVodTimeCode,
    onPlaying,
    onPlayPause,
    onAudioView,
    onSubtitlesView,
    onBuffering,
    onSeeked,
    onCompleted,
    onError,
    manageAudioProfile,
    bookmarked,
    rewindItem,
    onLoadedata,
    renderCustomButton,
    children,
    onFinish,
    onDestroy,
    isMuted = false,
    withSimpleUIInMinimizedMode,
    withControls = false,
    renderSkipIntro,
    renderSkipOutro,
    renderWatchCredit,
    streamingType,
    startOverPlayback,
    playerReadyCallback,
    sendBookmark,
    isTvod = false,
    toggleMute,
    header,
    isSwitch = false,
    onWatermarkReady,
    advertising,
}: GenericBitmovinPlayerProps) => {
    // refs
    const playerInitializedRef = useRef(false);
    const playerInstanceRef = useRef<PlayerAPI | null>(null);
    /**
     * The CDN session this component started and the player it attached to it. Every stop goes
     * through `cdnHandler.stopCDNSessionFor` with these, so this player never stops a session that
     * belongs to another one (e.g. the hero trailer behind a detail overlay).
     */
    const cdnSessionRef = useRef<unknown>(null);
    const cdnPlayerRef = useRef<PlayerAPI | null>(null);
    const stopOwnCDNSession = (reason = 'player stop') =>
        cdnHandler.stopCDNSessionFor(cdnPlayerRef.current, cdnSessionRef.current, `${playerType}: ${reason}`);
    const youboraInitializedRef = useRef(false);
    const uiManagerRef = useRef<UIManager | null>(null);
    /**
     * Ref that stores the last known playback time of the player.
     * Used when sending ad exit events to analytics services.
     */
    const lastKnownTimeRef = useRef(0);

    // configuration
    // const config = useConfigurationSections();
    const { config } = useConfigurationSections();
    const { player = {} } = config;
    const playerKey = (player as any)?.bitmovin_app_key;
    const playerDebug = (player as any)?.debug || false;

    // hooks
    const { track } = useContext(MixpanelContext);
    const { setBucket } = useCdnBalancer();
    const { isAdsEnabled, onAdBeginUI, resetAdsCounter, updateAdsUI, onAdBreakEndUI } = useAvod(
        playerType,
        isTvod,
        VODAsset
    );

    const { customUi } = useCustomUi({
        playerType,
        LinearAsset,
        withSimpleUIInMinimizedMode,
        renderCustomButton,
        renderSkipIntro,
        renderSkipOutro,
        renderWatchCredit,
        toggleMute,
        startOverPlayback,
    });

    const {
        streamUrl,
        state,
        playerWrapper,
        autoUiVariantResolve,
        setAutoUiVariantResolve,
        clearConcurrentLock,
        resolveInitialStream,
        setPlayerWrapper,
        handlePlayerOnAdSeeked,
        setStreamUrl,
        setState,
        setNextDelay,
    } = usePlayer({
        playerType,
        streamingType,
        VODAsset,
        LinearAsset,
        uiManagerRef,
        isTvod,
        bookmarkedVodTimeCode,
        onDestroy,
    });

    const waterMarkRef = useRef<WaterMark | null>(null);
    const onReady = (player) => {
        waterMarkRef.current = new WaterMark(player);
        waterMarkRef.current.setup();
        onWatermarkReady?.(waterMarkRef.current);

        if (playerReadyCallback) {
            playerReadyCallback(player);
        }

        lowLatencyDebug.attachToPlayer(player);
    };

    const {
        handlePlayerRef,
        handlePlayerOnloadeddata,
        handleFirstPlay,
        handlePlayerTimeChanged,
        handlePlayerOnFinish,
        handlePlayerOnDestroy,
        handlePlayerOnSeeked,
        handleOnStallEnded,
        handleOnAudioTrackChange,
        handleUIManagerReady,
        handlePlayerOnError,
    } = usePlayerHandler({
        playerInitializedRef,
        playerInstanceRef,
        uiManagerRef,
        youboraInitializedRef,
        playerType,
        streamUrl: state?.video?.resolvedUrl || streamUrl,
        VODAsset,
        LinearAsset,
        isAdsEnabled,
        manageAudioProfile,
        textTrackTimer,
        rewindItem,
        onAdBeginUI,
        onFinish,
        onCompleted,
        onDestroy,
        onSeeked,
        onBuffering,
        onAudioTrackChange,
        setState,
        onError,
        onTransitionEnd,
        onPlayPause,
        onPlaying,
        clearConcurrentLock,
        onSubtitlesView,
        onAudioView,
        playerReadyCallback: onReady,
        sendBookmark,
        bookmarkedVodTimeCode,
        bookmarked,
        onLoadedata,
    });

    const { addBookmark, removeBookmark } = useBookmark();
    const queryClient = useQueryClient();

    // TODO: move logic for setting/stopping learn manager to a hook
    const learnManagerRef = useRef<any>(null);

    const setLearnManager = (playerType, asset, track) => {
        const player = playerInstanceRef.current;
        if (!player || !asset || !isPlayerAlive(player)) return;

        const configType = playerType === playerTypes.LiveTV ? 'Linear' : 'VOD';
        const config = h.getLearningConfig(configType);

        if (config.enabled && learnManagerRef.current === null) {
            const manager = new LearnManager(player, playerType, asset, config, track);
            manager.init(asset);
            learnManagerRef.current = manager;
        }
    };

    const stopLearnManager = () => {
        const manager = learnManagerRef.current;
        if (!manager) return;
        manager.onDestroy?.();
        learnManagerRef.current = null;
    };

    const sendBookmarksOnPause = () => {
        sendBookmarks(VODAsset, playerService, playerType, addBookmark, removeBookmark);
    };

    const sendBookmarksOnSeek = (seekTarget: number) => {
        sendBookmarks(VODAsset, playerService, playerType, addBookmark, removeBookmark, seekTarget);
    };

    // effects
    useEffect(() => {
        cdnHandler.init(config);
        setBucket(playerType);
        const interval = setInterval(() => {
            const elementsToId = {
                '.bmpui-seekbar-backdrop': 'player-seekbar-id',
                '.bmpui-ui-settingstogglebutton': 'player-setting-button-id',
                '.bmpui-ui-fullscreentogglebutton': 'player-fullscreen-button-id',
                '.bp5-icon-close': 'player-close-button-id',
                '.bmpui-ui-volumetogglebutton': 'player-volume-control-button-id',
            };
            let allFound = true;
            for (const selector in elementsToId) {
                const el = document.querySelector(selector);
                if (el) {
                    el.setAttribute('id', elementsToId[selector]);
                    el.setAttribute('data-id', elementsToId[selector]);
                } else {
                    allFound = false;
                }
            }
            if (allFound) clearInterval(interval);
        }, 500);

        if (config.avodConfigs.midRoll?.enabled && !userAccessControl.isPremiumUser) {
            playerSSAIManager.init(playerType, uiManagerRef);
            if (playerSSAIManager.canRunSSAI) {
                setAutoUiVariantResolve(false);
                playerSSAIManager.onReady = (adPods, adList) => {
                    // Patch player duration/time so the seekbar and timer reflect content-only time
                    const player = playerService?.player;
                    if (player) {
                        playerSSAIManager.patchPlayerDuration(player);
                    }

                    const setLikesVisibility = (visible: boolean) => {
                        const likeDislikeBtns: HTMLDivElement | null = document.querySelector(
                            "#player-heading-block>[class^='like_likes']"
                        );
                        if (likeDislikeBtns) likeDislikeBtns.style.display = visible ? 'flex' : 'none';
                    };

                    const recordMixpanelEvent = (
                        eventName: string,
                        disableBaseParameters = false,
                        extraParameters: Record<string, any> = {}
                    ) => {
                        track(eventName, {
                            'User ID': userAccessControl.userInfo?.uid || userAccessControl.userInfo?.accountId || '',
                            'Ad Type': 'midroll',
                            'Content Type': 'Vod',
                            ...(disableBaseParameters
                                ? {}
                                : {
                                      'Midroll Ad ID': playerSSAIManager.currentPlayingAd.id,
                                      'Ad Sequence Number': playerSSAIManager.currentPlayingAd.position,
                                      'Ad Duration': playerSSAIManager.currentPlayingAd.durationSeconds,
                                  }),
                            ...getMixpanelVodProperties(VODAsset, null),
                            ...extraParameters,
                        });
                    };

                    if (adPods.length > 0) {
                        const hasPre = adPods.some((pod) => pod.startAt < 1);
                        const hasMid = adPods.some((pod) => pod.startAt >= 1);

                        const types: string[] = [];
                        if (hasPre) types.push('Pre');
                        if (hasMid) types.push('Mid');

                        const adType = types.length > 0 ? `${types.join('+')} roll` : 'No roll';

                        recordMixpanelEvent(EVENT.MIDROLL.AD_REQUESTED, true, {
                            'Ad Type': adType,
                            adTechnology: 'SSAI',
                            'Advertising Parameters': generateSupplyTagsForSSAI() || {},
                        });
                        recordMixpanelEvent(EVENT.MIDROLL.AD_RECEIVED, true, {
                            'Ad Type': adType,
                            'Ad Technology': 'SSAI',
                            'Content Type': 'VOD',
                            'Advertising Parameters': generateSupplyTagsForSSAI() || {},
                            'Ad Break Data': adList.length > 0 ? adList : adPods.length > 0 ? adPods : {},
                        });
                    }

                    playerSSAIManager.onAdPodStart = (adPod) => {
                        playerSSAIManager.saveCurrentUIState();
                        playerSSAIManager.switchToAdsUI();
                        setLikesVisibility(false);
                    };

                    playerSSAIManager.onAdStart = (ad) => {
                        recordMixpanelEvent(EVENT.MIDROLL.AD_START);
                    };

                    playerSSAIManager.onAdEnded = (ad) => {
                        recordMixpanelEvent(EVENT.MIDROLL.AD_COMPLETED);
                    };

                    playerSSAIManager.onAdPaused = (ad) => {
                        recordMixpanelEvent(EVENT.MIDROLL.USER_PAUSE_THE_AD, false, {
                            'Pause Time': playerSSAIManager.currentPlayerTime - ad.startAt,
                        });
                    };

                    playerSSAIManager.onAdResumed = (ad) => {
                        recordMixpanelEvent(EVENT.MIDROLL.USER_RESUME_THE_AD, false, {
                            'Resume Time': playerSSAIManager.currentPlayerTime - ad.startAt,
                        });
                    };
                    playerSSAIManager.onAdExit = (ad) => {
                        const adWatchedTime = playerSSAIManager.currentPlayerTime - ad.startAt;
                        const adCompletionPercentage = Math.floor((adWatchedTime / ad.endAt) * 100) + '%';
                        recordMixpanelEvent(EVENT.MIDROLL.USER_EXITED_DURING_AD, false, {
                            'Time Watched': adWatchedTime,
                            'Completion Rate': adCompletionPercentage,
                            'Exit Reason': 'Content exited',
                        });
                    };

                    playerSSAIManager.onAdPodEnded = (adPod) => {
                        playerSSAIManager.restoreSavedUIState();
                        setLikesVisibility(true);
                        recordMixpanelEvent(EVENT.MIDROLL.VIDEO_STARTED_AFTER_AD, true);
                    };

                    playerSSAIManager.onVisibilityChanged = (visible) => {
                        if (!playerSSAIManager.currentPlayingAdPod) return;
                        const player = playerService?.player;
                        if (!player) return;
                        if (visible && player.isPaused()) safePlayerPlay(player);
                        else if (!visible && player.isPlaying()) player.pause();
                    };
                    playerSSAIManager.onDestroyed = () => {
                        setAutoUiVariantResolve(true);
                        setLikesVisibility(true);
                    };
                };
            }
        }

        return () => {
            if (playerType === playerTypes.Vod) {
                // Final bookmark while player is still alive, then refresh Continue Watching.
                // Await bookmark write so getDetailBookmark does not race ahead of the last save.
                const bookmarkPromise = sendBookmarks(VODAsset, playerService, playerType, addBookmark, removeBookmark);
                void Promise.resolve(bookmarkPromise)
                    .catch(() => undefined)
                    .finally(() => {
                        queryClient.invalidateQueries({
                            queryKey: ['profiles', 'detailed-bookmarks'],
                        });
                    });
            }
            // clearConcurrentLock();

            stopOwnCDNSession('unmounted');
            avodMixpanelEvents.onAdExit(lastKnownTimeRef.current);
            avodHandler.destroy();
            playerSSAIManager.handlePlayerDestroyed();

            if (waterMarkRef.current) {
                waterMarkRef.current?.removeWatermarkOverlay();
                waterMarkRef.current.stop();
            }

            if (playerService) {
                if (textTrackTimer) {
                    clearTimeout(textTrackTimer);
                }

                stopLearnManager();
                resetAdsCounter(playerService.getPlayer());

                playerService.destroyPlayer();
                playerService.setPlayer(null);
                playerService.clearAdsData();
                clearInterval(interval);
            }
        };
    }, []);

    useEffect(() => {
        const handleVisibilityChange = () => {
            const player = playerService?.getPlayer?.();
            if (!player || !isPlayerAlive(player)) return;

            if (playerInitializedRef.current) {
                if (document.hidden) {
                    playerService.getPlayer().pause();
                } else {
                    safePlayerPlay(playerService.getPlayer());
                }
            }
        };
        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
    }, [playerInitializedRef.current]);

    useEffect(() => {
        const player = playerInstanceRef.current;

        if (!player || !isPlayerAlive(player)) return;

        if (playerWrapper?.initializedPlayer && (VODAsset || LinearAsset)) {
            setLearnManager(playerType, playerType === playerTypes.Vod ? VODAsset : LinearAsset, track);
        }
    }, [playerWrapper, playerInstanceRef.current, playerType, VODAsset, LinearAsset, track]);

    useEffect(() => {
        const determineStreamUrl = () => {
            if (playerType !== playerTypes.Vod || contentType === screenDetailTypes.MOVIES) {
                return initialStreamingUrl;
            }

            if (contentType === screenDetailTypes.SERIES || contentType === screenDetailTypes.EPISODE) {
                if (contentType === screenDetailTypes.SERIES) {
                    return playlist[defaultEpisodeIndex]?.streamingUrl || null;
                }
                const activeIndex = h.getIndexOfArrayByAttrVal(playlist, 'Id', atob(url.getQuery('id')));
                if (activeIndex === -1) {
                    url.insertParams({
                        id: btoa(playlist[defaultEpisodeIndex].Id),
                        guid: btoa(playlist[defaultEpisodeIndex].guid),
                    });
                    return playlist[defaultEpisodeIndex]?.streamingUrl || null;
                }
                return playlist[activeIndex]?.streamingUrl || null;
            }
        };
        setStreamUrl(determineStreamUrl());
    }, [playerType, contentType, playlist, defaultEpisodeIndex, initialStreamingUrl]);

    useEffect(() => {
        if (streamUrl) {
            // Start the CDN session before resolving so the player attaches to the same session the
            // URL was resolved through — `cdnHandler.resolveURL` opens one of its own otherwise.
            // Pass our previous session (episode or channel switch) so it is stopped; a session another
            // player is streaming from is left running.
            cdnSessionRef.current = cdnHandler.initiateSession(cdnSessionRef.current);
            cdnLog(`${playerType} stream start on ${describeSession(cdnSessionRef.current)}`, {
                url: shortUrl(streamUrl),
                isSwitch,
            });
            resolveInitialStream(isSwitch, cdnSessionRef.current);
        } else {
            setNextDelay(null);
        }
    }, [streamUrl]);

    /**
     * Create the player only once the stream has resolved. The low latency buffer `tweaks` are read
     * by Bitmovin at construction and cannot be changed afterwards, and whether the source is low
     * latency is only known from the resolved URL. The latency config itself (`live.lowLatency`)
     * travels on the source instead — see the `sourceConfig` memo below.
     */
    useEffect(() => {
        if (!state.video?.resolvedUrl) return;

        initiatePlayerWrapper();
    }, [state.video?.resolvedUrl]);

    const onSourceLoaded = () => {
        handlePlayerOnloadeddata();
        if (!learnManagerRef.current) {
            setLearnManager(playerType, playerType === playerTypes.Vod ? VODAsset : LinearAsset, track);
        }
    };

    const { isAdsEnabledForCSAI, adBreaksForCSAI } = useCSAI(
        (LinearAsset || VODAsset) as any,
        playerType as keyof typeof playerTypes,
        'LINEAR'
    );
    /** CSAI Ad breaks */
    const adBreaks = useMemo(() => {
        return isAdsEnabledForCSAI ? adBreaksForCSAI : [];
    }, [isAdsEnabledForCSAI]);

    const onTimeChanged = (event) => {
        lastKnownTimeRef.current = event.time;
        handlePlayerTimeChanged(event);
    };

    const ssaiAdMarkers = config?.avodConfigs?.enableAdsMarkers ? playerSSAIManager.markers : [];

    /**
     * Only known once the stream resolves, which is why the player is created from the
     * `state.video.resolvedUrl` effect above — the buffer `tweaks` have no runtime setter, so they
     * have to be right at `new Player()` time.
     */
    const isLowLatencyStream = isLowLatencySource(state.video?.resolvedUrl);

    const lowLatencyDebug = useLowLatencyDebug(isLowLatencyStream, playerInstanceRef);

    const playbackRateThreshold = 0.075;

    /**
     * Supplying this config is what turns latency management on: the player has no way to tell a low
     * latency stream from a regular one, it simply acts on whatever `live.lowLatency` it is given.
     * Hence the `isLowLatencyStream` gate — on a regular live stream these thresholds are
     * unreachable and the player would seek and rate-change in a loop trying to hit them.
     *
     * Values diverge from Bitmovin's defaults (`seekThreshold: 5` on both, catchup
     * `playbackRate: 1.2`) deliberately: we seek back to the target sooner and lean less on speeding
     * playback up, which is less audible on live sport.
     */
    const lowLatencyLiveConfig = {
        lowLatency: {
            targetLatency: 3,
            catchup: {
                playbackRateThreshold,
                seekThreshold: 3,
                playbackRate: 1.1,
            },
            fallback: {
                playbackRateThreshold,
                seekThreshold: 2,
                playbackRate: 0.95,
            },
        },
        synchronization: [
            {
                method: LiveSynchronizationMethod.HttpHead,
                serverUrl: 'https://time.akamai.com',
            },
        ],
    };

    /**
     * `adaptation.logic` is deliberately left unset for low latency sources. Bitmovin's
     * `PlayerConfigEnhancer` sets `LOW_LATENCY` itself on every source load where the manifest is
     * DASH + live and a `lowLatency` config is present, and it does so only when we have not pinned
     * a logic ourselves. Pinning it here would make it stick to every later source too — including
     * regular channels — because the enhancer restores our constructor value before re-deriving.
     */
    let adaptationConfig: AdaptationPlatformConfig = { desktop: { preload: true }, mobile: { preload: true } };

    if (isLowLatencyStream) {
        adaptationConfig = {
            preload: false,
            // Encourage switching to a higher quality sooner
            qualityStabilityBalance: 0.75,
        };
    }

    /**
     * Buffer tweaks only. These have no runtime setter, so unlike `live.lowLatency` they belong on
     * the player config. `CHUNKED_CMAF_STREAMING` is intentionally absent: the enhancer defaults it
     * to `true` for low latency DASH sources, and setting it here would override that per-source
     * decision with a constructor-time one.
     */
    const lowLatencyTweaks = {
        RESTART_THRESHOLD: 0.2,
        RESTART_THRESHOLD_DELTA: 0.05,
        STARTUP_THRESHOLD: 0.2,
        STARTUP_THRESHOLD_DELTA: 0.05,
        END_OF_BUFFER_TOLERANCE: 0.05,
        LIVE_EDGE_DISTANCE: 0.5,
        LOW_LATENCY_BUFFER_GUARD: 0.8,
    };

    const playerConfig: PlayerConfig = {
        key: playerKey,
        playback: { autoplay: true, muted: isMuted },
        events: usePlayerEvents(
            playerType === playerTypes.LiveTV || playerType === playerTypes.Catchup ? LinearAsset : VODAsset,
            handlePlayerOnError,
            handlePlayerOnFinish,
            handlePlayerOnDestroy,
            handlePlayerOnSeeked,
            handleOnStallEnded,
            onSourceLoaded,
            handleOnAudioTrackChange,
            handleFirstPlay,
            handlePlayerOnAdSeeked,
            onTimeChanged,
            sendBookmarksOnPause,
            sendBookmarksOnSeek,
            stopOwnCDNSession
        ),
        advertising: { adBreaks },
        ui:
            playerType === playerTypes.Trailer && !withControls
                ? false
                : { metadata: { markers: [...ssaiAdMarkers] }, autoUiVariantResolve },
        logs: { level: playerDebug && LogLevel.DEBUG, bitmovin: playerDebug },
        tweaks: { fairplay_ignore_duplicate_init_data_key_errors: true },
        adaptation: adaptationConfig,
    } as PlayerConfig;

    if (isLowLatencyStream) {
        playerConfig.tweaks = { ...playerConfig.tweaks, ...lowLatencyTweaks };
    }

    if (advertising) {
        playerConfig.advertising = advertising;
    }

    const sourceConfig = useMemo(() => {
        if (!state.video?.resolvedUrl) return null;
        const poster = playerType === playerTypes.Vod ? VODAsset?.image_landscape : LinearAsset?.poster;
        return {
            dash: state.video.resolvedUrl,
            hls: state.video.resolvedUrl,
            drm: getDRMConfig(),
            poster: poster ? h.getImagizerUrl(poster) : '',
            title: LinearAsset?.title || '',
            /**
             * Latency tuning is opt-in per source: regular streams keep the manifest's own
             * suggestedPresentationDelay instead of being pulled towards the live edge. Source level
             * config is supported since player 8.176.0 and overrides the player level one, so this
             * is scoped to exactly the stream it was resolved for.
             */
            ...(isLowLatencyStream ? { live: lowLatencyLiveConfig } : {}),
        };
    }, [state.video?.resolvedUrl]);

    const initiatePlayerWrapper = useCallback(() => {
        const bitmovinPlayer = createBitmovinPlayer(playerConfig);
        cdnPlayerRef.current = bitmovinPlayer.initializedPlayer;
        cdnLog(`${playerType} player ${describePlayer(bitmovinPlayer.initializedPlayer)} created`, {
            session: describeSession(cdnSessionRef.current),
        });
        cdnHandler.attachPlayerToCDNSession(bitmovinPlayer.initializedPlayer, cdnSessionRef.current);
        if (playerSSAIManager.canRunSSAI) cdnHandler.setupEventsForSSAI();
        setPlayerWrapper(bitmovinPlayer);
    }, [isAdsEnabled, JSON.stringify(playerConfig)]);

    const onBeforeSourceLoaded = (source) => {
        if (adBreaks && adBreaks.length) {
            avodMixpanelEvents.onAdRequested();
        }
    };

    const PlayerComponent = useMemo(() => {
        if (!sourceConfig || !playerWrapper) return null;
        return (
            <BaseBitmovinPlayer
                playerRef={handlePlayerRef}
                onUIManagerReady={handleUIManagerReady}
                config={playerConfig}
                source={sourceConfig}
                customUi={customUi}
                className={`bitmovin-player bitmovinplayer-container ${
                    (withSimpleUIInMinimizedMode || playerType === playerTypes.Trailer) &&
                    // TODO: review this condition and the whole logic for applying `object-fit: cover`. It's too complicated
                    withSimpleUIInMinimizedMode != (playerType === playerTypes.Trailer)
                        ? 'video-object-cover'
                        : ''
                }`}
                playerWrapper={playerWrapper}
                onBeforeSourceLoaded={onBeforeSourceLoaded}
            />
        );
    }, [sourceConfig, playerWrapper, customUi]);

    if (!state.video?.resolvedUrl && !state.error) {
        return (
            <span className={`${styles.holder} ${styles['spinner-holder']}`}>
                <Spinner opaqueBg block />
            </span>
        );
    }

    return (
        <div id="video-container" className={styles.holder}>
            {state.error ? <PlayerError error={state.error} /> : state.video?.resolvedUrl && PlayerComponent}
            {header && (
                <FlexWrapper
                    direction="row"
                    gap="3x"
                    justifyContent="between"
                    id="player-heading-block"
                    className={styles.heading}
                    paddingTop={{ default: '2x', md: '4x' }}
                    paddingStart={{ default: '2x', md: '6x' }}
                    paddingEnd={{ default: '2x', md: '6x' }}
                    style={{ margin: '0' }}
                >
                    {header}
                    {/* disabled for release */}
                    {/* <LowLatencyDebugPanel
                        isLowLatency={lowLatencyDebug.isLowLatency}
                        showPanel={lowLatencyDebug.showPanel}
                        hidePanel={lowLatencyDebug.hidePanel}
                        targetLatency={lowLatencyDebug.targetLatency}
                        handleTargetLatencyChange={lowLatencyDebug.handleTargetLatencyChange}
                    /> */}
                </FlexWrapper>
            )}
            {children}
        </div>
    );
};

export default GenericBitmovinPlayer;
